/**
 * ProfileBanner — the banner/cover area behind the avatar and identity
 * block. Renders the reader's uploaded image (object-fit: cover, never
 * distorted) or, when none is set, a designed ScribbleReads default:
 * a soft tone-gradient, the existing paper-grain texture, and a couple of
 * the app's own hand-drawn motifs at very low opacity — literary and
 * on-brand rather than a blank rectangle or a stock photo.
 */

import React from 'react';
import { Camera } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { CircleScribble, FloatingBook } from '@/components/site/Scribbles';
import { PROFILE } from '@/constants/testIds';

const COVER_TONE = {
  coral: 'from-coral/35 via-yellow/20 to-cream',
  yellow: 'from-yellow/40 via-sage/20 to-cream',
  sage: 'from-sage/40 via-blue/20 to-cream',
  blue: 'from-blue/35 via-coral/15 to-cream',
};

export default function ProfileBanner({ tone = 'coral', bannerImage, isOwnProfile, onEdit }) {
  return (
    <div
      data-testid={PROFILE.banner}
      className="relative h-36 w-full overflow-hidden rounded-b-[28px] sm:h-48 md:h-56"
    >
      {bannerImage ? (
        <img
          src={bannerImage}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <div className={cx('relative h-full w-full bg-gradient-to-br', COVER_TONE[tone] || COVER_TONE.coral)} aria-hidden="true">
          <div className="grain absolute inset-0" />
          <CircleScribble className="absolute -right-6 -top-6 h-32 w-56 text-ink/10 sm:h-40 sm:w-72" />
          <FloatingBook className="absolute -bottom-2 left-6 h-10 w-12 opacity-15 sm:h-14 sm:w-16" />
        </div>
      )}

      {isOwnProfile && (
        <button
          type="button"
          onClick={onEdit}
          data-testid={PROFILE.bannerEditButton}
          aria-label={bannerImage ? 'Change banner image' : 'Add a banner image'}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-black/50"
        >
          <Camera className="h-3.5 w-3.5" /> {bannerImage ? 'Change Banner' : 'Add Banner'}
        </button>
      )}
    </div>
  );
}
